import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import styles from './OverlayScrollbar.module.scss'

interface OverlayScrollbarProps {
  /** The scroll container this bar drives */
  targetRef: RefObject<HTMLElement | null>
}

interface Metrics {
  /** Thumb offset and length as a share of the track, 0-100 */
  top: number
  height: number
  scrollable: boolean
}

const MIN_THUMB_PERCENT = 8

/**
 * A scrollbar drawn ON TOP of the content instead of beside it.
 *
 * A native `::-webkit-scrollbar` occupies layout space, so widening it on hover
 * reflows everything it sits next to, in a week calendar that shifts all seven
 * day columns under the pointer. This one is an absolutely positioned thumb, so
 * the content keeps the full width and the bar can grow over it.
 */
function OverlayScrollbar(props: OverlayScrollbarProps) {
  const { targetRef } = props

  const [metrics, setMetrics] = useState<Metrics>({
    top: 0,
    height: 0,
    scrollable: false
  })
  const [isDragging, setIsDragging] = useState(false)

  const trackRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{
    pointerId: number
    originY: number
    originScrollTop: number
  } | null>(null)

  const measure = useCallback(() => {
    const element = targetRef.current
    if (!element) return

    const { scrollHeight, clientHeight, scrollTop } = element
    const scrollable = scrollHeight > clientHeight + 1

    setMetrics({
      scrollable,
      height: scrollable
        ? Math.max((clientHeight / scrollHeight) * 100, MIN_THUMB_PERCENT)
        : 0,
      top: scrollable ? (scrollTop / scrollHeight) * 100 : 0
    })
  }, [targetRef])

  useEffect(() => {
    const element = targetRef.current
    if (!element) return undefined

    measure()

    element.addEventListener('scroll', measure, { passive: true })

    // Content grows and shrinks with the window and with the shifts rendered
    // into it, so the thumb is re-measured from both.
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    Array.from(element.children).forEach(child => observer.observe(child))

    return () => {
      element.removeEventListener('scroll', measure)
      observer.disconnect()
    }
  }, [targetRef, measure])

  useEffect(() => {
    if (!isDragging) return undefined

    const handleMove = (event: PointerEvent) => {
      const drag = dragRef.current
      const element = targetRef.current
      const track = trackRef.current
      if (!drag || !element || !track || drag.pointerId !== event.pointerId) {
        return
      }

      const trackHeight = track.getBoundingClientRect().height
      if (!trackHeight) return

      // A pixel of track is worth scrollHeight/trackHeight pixels of content
      const ratio = element.scrollHeight / trackHeight
      element.scrollTop = drag.originScrollTop + (event.clientY - drag.originY) * ratio
    }

    const handleUp = (event: PointerEvent) => {
      if (dragRef.current?.pointerId !== event.pointerId) return
      dragRef.current = null
      setIsDragging(false)
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleUp)

    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
    }
  }, [isDragging, targetRef])

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = targetRef.current
    if (event.button !== 0 || !element) return

    event.preventDefault()
    dragRef.current = {
      pointerId: event.pointerId,
      originY: event.clientY,
      originScrollTop: element.scrollTop
    }
    setIsDragging(true)
  }

  if (!metrics.scrollable) return null

  return (
    <div
      ref={trackRef}
      className={`${styles.track} ${isDragging ? styles.trackActive : ''}`}
      aria-hidden="true"
    >
      <div
        className={`${styles.thumb} ${isDragging ? styles.thumbActive : ''}`}
        style={{ top: `${metrics.top}%`, height: `${metrics.height}%` }}
        onPointerDown={handleThumbPointerDown}
      />
    </div>
  )
}

export default OverlayScrollbar
